import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountService } from '../../system/accounts/service/account.service';
import { TransitionsService } from '../../system/workflows/service/transitions.service';
import { NodeTransitionService } from '../service/node-transition.service';

@Component({
    selector: 'app-node-transition',
    templateUrl: './node-transition.component.html',
    styleUrls: ['./node-transition.component.scss']
})
export class NodeTransitionComponent implements OnInit {
    @Input() node: any;
    @Input() disabled: boolean = false;
    @Output() chosenToStateChange: EventEmitter<any> = new EventEmitter<any>();

    @Input() transitionStorage: any;
    @Output() transitionStorageChange: EventEmitter<any> = new EventEmitter<any>();

    // --- Form & State ---
    transitionForm!: FormGroup;
    transitionSubmitted = false;

    inCompletionState = false;
    disableSendToQueue = false;
    loadingUsers = false;
    users: any[] = [];

    // transitionStorage: any;

    showUserErrorMessage = false;
    userErrorMessage = '';

    constructor(
        private fb: FormBuilder,
        private nodeTransition: NodeTransitionService,
        private transitionsService: TransitionsService,
        private accountService: AccountService
    ) {}

    // -------------------------------
    // 🔹 Lifecycle
    // -------------------------------
    ngOnInit(): void {
        this.nodeTransition.reset();
        this.transitionStorage = this.nodeTransition.storage;
        this.transitionStorageChange.emit(this.transitionStorage);
        this.initForm();
        this.init();
    }

    private initForm(): void {
        this.transitionForm = this.fb.group({
            transition: ['', Validators.required],
            assignToUser: ['']
        });

        // When transition select changes
        this.transitionForm.get('transition')?.valueChanges.subscribe((value) => {
            this.transitionStorage.chosenToState = value;
            this.chosenToStateChange.emit();
            this.onStateChange();
        });

        // When user select changes
        this.transitionForm.get('assignToUser')?.valueChanges.subscribe((value) => {
            this.transitionStorage.assignToUser = value;
        });
    }

    private init(): void {
        this.getTransitions();
        if (this.node?.WORKFLOW_STATE_TYPES?.NAME === 'Completion State') {
            this.inCompletionState = true;
        }
    }

    private getTransitions(): void {
        this.nodeTransition.getTransitions(this.node);
    }

    // -------------------------------
    // 🔹 Main Actions
    // -------------------------------
    nodeTransitionAction(): void {
        this.transitionSubmitted = true;
        if (this.transitionForm.invalid) return;
        this.nodeTransition.transition(this.node);
    }

    onStateChange(): void {
        this.users = [];
        this.transitionStorage.assignToUser = undefined;
        this.showUserErrorMessage = false;

        const chosen = this.transitionStorage.chosenToState;
        if (!chosen) return;

        const transitions = this.transitionStorage.transitions;
        const t = transitions.find((t: any) => t.WORKFLOW_STATE_TYPES1.ID === chosen.ID);
        if (!t) return;

        this.disableSendToQueue = this.hasPropertyType(t, 'Disable Assign To Queue');

        if (this.hasPropertyType(t, 'Fixed User Transitions Only')) {
            this.getFixedUsersByRegion();
        } else if (this.hasPropertyType(t, 'Assign To Creator Only') && this.hasPropertyType(t, 'Assign To Last Editor Only')) {
            this.getCreatorAndLastEditor();
        } else if (this.hasPropertyType(t, 'Assign To Creator Only')) {
            this.getCreator();
        } else if (this.hasPropertyType(t, 'Assign To Last Editor Only')) {
            this.getLastEditor();
        } else if (this.hasPropertyType(t, 'Assign To User')) {
            this.getUsersByRegion();
        }
    }

    hasPropertyType(transition: any, type: string): boolean {
        return transition.TRANSITION_PROPERTIES?.some((p: any) => p.TRANSITION_PROPERTY_TYPES?.TYPE === type);
    }

    // -------------------------------
    // 🔹 User Assignment Logic
    // -------------------------------
    private getLastEditor(): void {
        const histories = this.node?.NODE_HISTORIES || [];
        if (histories.length === 0) return;

        const lastHistory = histories[histories.length - 1];
        const loginDetailsId = lastHistory?.LOGIN_DETAILS?.ID;

        if (!loginDetailsId) return;

        this.accountService.checkAccountStatus(loginDetailsId).subscribe({
            next: (res) => {
                if (res.msg?.SUCCESS) {
                    this.users = [lastHistory.LOGIN_DETAILS];
                    this.transitionForm.patchValue({ assignToUser: this.users[0] });
                } else {
                    this.showErrorForUser(lastHistory.LOGIN_DETAILS, res.accountStatus);
                }
            },
            error: () => {}
        });
    }

    private getCreator(): void {
        const creators = this.node?.NODE_CREATORS || [];
        if (creators.length === 0) return;

        const creator = creators[0];
        const loginDetailsId = creator?.LOGIN_DETAILS?.ID;

        this.accountService.checkAccountStatus(loginDetailsId).subscribe({
            next: (res) => {
                if (res.msg?.SUCCESS) {
                    this.users = [creator.LOGIN_DETAILS];
                    this.transitionForm.patchValue({ assignToUser: this.users[0] });
                } else {
                    this.showErrorForUser(creator.LOGIN_DETAILS, res.accountStatus);
                }
            }
        });
    }

    private getCreatorAndLastEditor(): void {
        const creators = this.node?.NODE_CREATORS || [];
        const histories = this.node?.NODE_HISTORIES || [];

        if (creators.length === 0 || histories.length === 0) return;

        const creator = creators[0].LOGIN_DETAILS;
        const lastEditor = histories[histories.length - 1].LOGIN_DETAILS;

        if (creator?.ID !== lastEditor?.ID) return;

        this.accountService.checkAccountStatus(creator.ID).subscribe({
            next: (res) => {
                if (res.msg?.SUCCESS) {
                    this.users = [creator];
                    this.transitionForm.patchValue({ assignToUser: creator });
                } else {
                    this.showErrorForUser(creator, res.accountStatus);
                }
            }
        });
    }

    private getUsersByRegion(): void {
        const region = this.node?.NODE_REGIONS?.[0]?.REGION;
        if (!region) return;

        this.loadingUsers = true;
        this.transitionsService.indexUsersByRegion(this.node, this.transitionStorage.chosenToState, region).subscribe({
            next: (res) => {
                this.loadingUsers = false;
                if (res.msg?.SUCCESS) {
                    this.users = res.users;
                    this.tryPreselectUser();
                } else {
                    this.showUserErrorMessage = true;
                    this.userErrorMessage = 'No users found to assign to.';
                }
            },
            error: () => {
                this.loadingUsers = false;
                this.showUserErrorMessage = true;
                this.userErrorMessage = 'Error retrieving users to assign to.';
            }
        });
    }

    private getFixedUsersByRegion(): void {
        const region = this.node?.NODE_REGIONS?.[0]?.REGION;
        if (!region) return;

        this.loadingUsers = true;
        this.transitionsService.indexFixedUsersByRegion(this.node, this.transitionStorage.chosenToState, region).subscribe({
            next: (res) => {
                this.loadingUsers = false;
                if (res.msg?.SUCCESS) {
                    this.users = res.users;
                    this.transitionForm.patchValue({ assignToUser: this.users[0] });
                } else {
                    this.showUserErrorMessage = true;
                    this.userErrorMessage = 'No users found to assign to.';
                }
            },
            error: () => {
                this.loadingUsers = false;
                this.showUserErrorMessage = true;
                this.userErrorMessage = 'Error retrieving users to assign to.';
            }
        });
    }

    private tryPreselectUser(): void {
        const histories = this.node?.NODE_HISTORIES || [];
        for (let i = histories.length - 1; i >= 0; i--) {
            const lastEditorId = histories[i]?.LOGIN_DETAILS?.ID;
            const foundUser = this.users.find((u: any) => u.ID === lastEditorId);
            if (foundUser) {
                this.transitionForm.patchValue({ assignToUser: foundUser });
                break;
            }
        }
    }

    private showErrorForUser(user: any, status: string): void {
        this.showUserErrorMessage = true;
        this.userErrorMessage = `Unable to assign to ${user?.PERSON?.FIRST_NAME} ${user?.PERSON?.LAST_NAME} (${user?.USER_ID}) [${status}]`;
    }

    // -------------------------------
    // 🔹 Template Helpers
    // -------------------------------
    getTransitionMessage(): string {
        const chosen = this.transitionStorage.chosenToState;
        const assigned = this.transitionStorage.assignToUser;

        if (!chosen) return '';
        const action = chosen.ACTION || '';
        const assignText = assigned
            ? `<br /> Assign to <strong>${assigned.PERSON.FIRST_NAME} ${assigned.PERSON.LAST_NAME}</strong>`
            : this.hasAssignToUser()
            ? '<br /> Send to Queue'
            : '';

        return `Next Step: <strong>${action}</strong>${assignText}`;
    }

    hasAssignToUser(): boolean {
        const chosen = this.transitionStorage.chosenToState;
        if (!chosen) return false;

        const transitions = this.transitionStorage.transitions || [];
        const t = transitions.find((t: any) => t.WORKFLOW_STATE_TYPES1.ID === chosen.ID);
        return !!t && (t.ASSIGN_USER === true || t.ASSIGN_USER === 'True');
    }
}
